import { get, isFunction, isString } from "radash"

import { log } from "@be-miniapp/logging"
import { useLocationPopupSetting } from "@be-miniapp/shared/stores/location-popup"

import { useUserPersistingData } from "@/store/user-persisting-data"

export const LOCATION_PERMISSION_RESPONSE_EVENT =
  "appview:location-permission-response"

interface LocationPermissionResponseDetail {
  isGranted: boolean
}

type NativeLocationGrantHandler = () => void

const nativeLocationGrantHandlers = new Set<NativeLocationGrantHandler>()
let isNativeLocationBridgeInitialized = false

function emitLocationPermissionResponse(isGranted: boolean) {
  window.dispatchEvent(
    new CustomEvent<LocationPermissionResponseDetail>(
      LOCATION_PERMISSION_RESPONSE_EVENT,
      {
        detail: { isGranted },
      }
    )
  )
}

export function isNativeLocationBridgeAvailable(): boolean {
  return Boolean(
    get(window, "webkit.messageHandlers.requestLocation.postMessage", null) ||
      get(window, "android.requestLocation", null) ||
      get(window, "flutter_inappwebview.callHandler", null) ||
      window.NativeBridge?.available
  )
}

export function registerNativeLocationGrantHandler(
  handler: NativeLocationGrantHandler
) {
  nativeLocationGrantHandlers.add(handler)

  return () => {
    nativeLocationGrantHandlers.delete(handler)
  }
}

function handleNativeLocationPermissionGranted() {
  useUserPersistingData.getState().setLocationPermissionState("accepted")
  useLocationPopupSetting.getState().dismiss()
  nativeLocationGrantHandlers.forEach((handler) => {
    handler()
  })
}

function handleNativeLocationPermissionDenied() {
  useUserPersistingData.getState().setLocationPermissionState("denied")
  useLocationPopupSetting.getState().showPermissionDenied()
}

function handleNativeBridgeMessage(message: string) {
  let parsedMessage: { isGrantedPermission?: unknown }

  try {
    const rawMessage = message as unknown
    parsedMessage = isString(rawMessage)
      ? JSON.parse(rawMessage)
      : (rawMessage as { isGrantedPermission?: unknown })
  } catch (error) {
    log({
      category: "location-permission",
      level: "error",
      message: "Failed to parse native location permission response",
      data: { error, message },
    })
    return
  }

  if (!("isGrantedPermission" in parsedMessage)) {
    log({
      category: "location-permission",
      level: "warning",
      message: "Native location permission response missing isGrantedPermission",
      data: parsedMessage,
    })
    return
  }

  const isGranted = Boolean(parsedMessage.isGrantedPermission)
  emitLocationPermissionResponse(isGranted)

  if (isGranted) {
    handleNativeLocationPermissionGranted()
    return
  }

  handleNativeLocationPermissionDenied()
}

export function initializeNativeLocationBridge() {
  if (typeof window === "undefined") {
    return
  }

  window.nativeBridgeMessageHandler = handleNativeBridgeMessage

  if (isNativeLocationBridgeInitialized) {
    return
  }

  isNativeLocationBridgeInitialized = true

  if (!window.NativeBridge) {
    window.NativeBridge = {
      available: isFunction(
        get(window, "flutter_inappwebview.callHandler", null)
      ),
      postMessage: (message: string) => {
        if (window.flutter_inappwebview?.callHandler) {
          window.flutter_inappwebview.callHandler("NativeBridge", message)
        } else {
          console.error("Flutter:NativeBridge not ready")
        }
      },
      messageHandler: (message: string) => {
        window.nativeBridgeMessageHandler?.(message)
      },
    }
  }

}
